'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Divider,
  Grid,
  Paper,
  Snackbar,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import Typography from '@mui/material/Typography';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';

import { GoogleCaptchaWrapper } from '@client/src/app/GoogleCaptchaWrapper';
import { Modal } from '@client/src/components/new-design/modal';
import graphqlFetch from '@client/src/data-access/graphql-fetch.client';
import { useUser } from '@client/src/hooks/use-user';
import { SEND_EMAIL_MESSAGE_TO_1SECURITY } from '@client/src/queries/sendEmailMessageTo1Security';

// Define form schema with Zod
const contactFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  message: z.string().min(5, 'Message must be at least 5 characters'),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

function ContactButtonContent({
  withAuth = true,
  buttonCTA,
  variant,
  alternateStyle = false,
}: {
  withAuth?: boolean;
  buttonCTA?: string;
  variant?: 'contained' | 'outlined';
  alternateStyle?: boolean;
}) {
  const { user } = useUser({ withAuth });
  const mdUp = useResponsive('up', 'md');
  const [isOpen, setIsOpen] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const t = useTranslations('components.contact');
  const tUpgrade = useTranslations('components.upgrade');

  // States for handling submission and notifications
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      email: user?.email || '',
      message: '',
    },
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        // Reset form and states when closing
        reset();
        setSuccessMessage('');
        setErrorMessage('');
      }
    },
    [reset],
  );

  const onSubmit = async (values: ContactFormValues) => {
    try {
      setSubmitting(true);

      if (!executeRecaptcha) {
        console.error('ReCaptcha not loaded');
        setErrorMessage(t('recaptchaError'));
        return;
      }

      const recaptchaToken = await executeRecaptcha('contactRequest');

      // Send the contact form mutation
      const response = await graphqlFetch({
        query: SEND_EMAIL_MESSAGE_TO_1SECURITY,
        variables: {
          message: values.message,
          email: values.email,
          recaptchaToken,
        },
        withAuth,
      });

      if (response.data.sendEmailMessageTo1Security) {
        setSuccessMessage(t('successMessage'));
        reset();
        // Close the modal after successful submission
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      } else {
        setErrorMessage(t('errorMessage'));
      }
    } catch (error) {
      console.error('Error sending contact message:', error);
      setErrorMessage(t('errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCalendarClick = () => {
    // Open Calendly in a new tab
    window.open('https://calendar.app.google/3TfcUe7AKzCV6Ynj6', '_blank');
  };

  // Using the same styling as in the dashboard header
  const renderButton = alternateStyle ? (
    // Alternate style matching the landing header styling with plain button and className
    <button
      onClick={() => setIsOpen(true)}
      className="no-underline !font-[700] text-new-text-800 visited:!text-new-text-900 hover:!text-new-deepPurple-500 bg-transparent border-none p-0 cursor-pointer"
    >
      {t(buttonCTA || 'title')}
    </button>
  ) : (
    // Default style
    <Button
      onClick={() => setIsOpen(true)}
      startIcon={<Iconify icon="mdi:contact-outline" className="text-white" />}
      color="secondary"
      sx={{
        borderRadius: 1,
        bgcolor: '#4526DB', // Secondary main color from palette
        '&:hover': {
          bgcolor: '#25139D', // Secondary dark color from palette
        },
        fontWeight: 600, // Ensure consistent font weight
        padding: '6px 12px',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: 'white',
          fontWeight: 600, // Ensure consistent font weight
          fontSize: '0.875rem', // Ensure consistent font size
        }}
      >
        {t(buttonCTA || 'title')}
      </Typography>
    </Button>
  );

  return (
    <>
      {renderButton}
      {/* {mdUp ? (
        renderButton
      ) : (
        <Iconify
          icon="mdi:contact-outline"
          onClick={() => setIsOpen(true)}
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 24,
            height: 24,
          }}
        />
      )} */}

      <Modal
        triggerElement={null}
        title={t('title')}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        className="w-[600px]"
      >
        <Box sx={{ py: 2 }}>
          {!successMessage && (
            <>
              {/* Calendly Option */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {t('scheduleCall')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t('scheduleCallDescription')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Iconify icon="mdi:calendar" />}
                  onClick={handleCalendarClick}
                  sx={{ minWidth: 200 }}
                >
                  {t('scheduleCall')}
                </Button>
              </Paper>

              {/* Divider with OR text */}
              <Box sx={{ position: 'relative', my: 4, textAlign: 'center' }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: '-10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'background.paper',
                    px: 2,
                    color: 'text.secondary',
                  }}
                >
                  {t('orSeparator')}
                </Typography>
              </Box>

              {/* Contact Form Title */}
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {t('contactFormTitle')}
              </Typography>

              {/* Contact Form */}
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t('email')}
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          disabled={submitting}
                          margin="normal"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="message"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={4}
                          label={t('message')}
                          error={!!errors.message}
                          helperText={errors.message?.message}
                          disabled={submitting}
                          margin="normal"
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      sx={{ minWidth: 'fit-content' }}
                    >
                      {submitting ? tUpgrade('sending') : t('send')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </>
          )}

          {successMessage && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Iconify
                icon="eva:checkmark-circle-2-fill"
                sx={{ width: 60, height: 60, color: 'success.main', mb: 2 }}
              />
              <Typography variant="h5" gutterBottom>
                {t('thankYouForContacting')}
              </Typography>
              <Typography variant="body1">{successMessage}</Typography>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Snackbar for error messages */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

const ContactButton = ({
  withAuth,
  buttonCTA,
  variant,
  alternateStyle,
}: {
  withAuth?: boolean;
  buttonCTA?: string;
  variant?: 'contained' | 'outlined';
  alternateStyle?: boolean;
}) => {
  return (
    <GoogleCaptchaWrapper>
      <ContactButtonContent
        withAuth={withAuth}
        buttonCTA={buttonCTA}
        variant={variant}
        alternateStyle={alternateStyle}
      />
    </GoogleCaptchaWrapper>
  );
};

export { ContactButton };
